import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getErrorMessage } from "../../../../utils/errorHandler";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Checkbox,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ShoppingCart, ArrowDown2, Gallery, TickCircle, SearchNormal1, Refresh } from "iconsax-react";
import { fetchCatalogs } from "../../../../redux/Slice/Catalog/catalogSlice";

/** Meta/WhatsApp API allows at most 30 products in a catalog message */
const MAX_CATALOG_PRODUCTS = 30;
const MAX_SECTION_TITLE = 24;

/** Stable identity for a product across live catalog products and previously saved selections. */
const productKey = (p) => p.retailerId || p.retailer_id || p.product_retailer_id || p.id || "";

const catalogKey = (catalog) => catalog?.id || catalog?._id || "";

const hasValidImage = (url) =>
  !!url && url !== "null" && url !== "undefined" && (url.startsWith("http") || url.startsWith("data:"));

const filterProductsBySearch = (products, term) => {
  const query = term.trim().toLowerCase();
  if (!query) return products;
  return products.filter((product) =>
    [product.name, product.description, product.retailerId].some((field) =>
      field?.toLowerCase().includes(query)
    )
  );
};

const NO_CATALOGS_HINT = {
  instagram:
    "No product catalogs found for this workspace. Connect a Facebook Business with a catalog (Commerce Manager) linked to your Page or Instagram.",
  facebook:
    "No product catalogs found for this workspace. Connect a Facebook Business with a catalog (Commerce Manager) linked to your Page or Instagram.",
  whatsapp:
    "No product catalogs found. Create a catalog in your WhatsApp Business account (Catalog Manager) and add products, then refresh.",
};

const buttonSx = { textTransform: "none", borderRadius: "8px", fontSize: "0.8125rem", fontWeight: 500 };

/**
 * CatalogSend
 * Catalog + product picker embedded in the workflow action drawers (WhatsApp / Instagram /
 * Facebook). Same look as the inbox catalog dialog (InboxCatalogSend.jsx) -- catalog dropdown,
 * product search, image cards -- but keeps the workflow contract: nothing is applied until
 * "Confirm selection", which calls onCatalogSelect with the catalog carrying only the picked
 * products (plus sectionTitle and type single/multi).
 */
const CatalogSend = ({ onCatalogSelect, selectedCatalog: externalSelectedCatalog, platform, compactLayout = false, minProductWidth, columns }) => {
  const dispatch = useDispatch();
  // platform: 'instagram' | 'facebook' | undefined (WhatsApp) — for catalog API so correct token is used per Meta
  const theme = useTheme();

  const { catalogs, status, error } = useSelector((state) => state.catalog);

  const [catalogId, setCatalogId] = useState(catalogKey(externalSelectedCatalog));
  const [selectedProducts, setSelectedProducts] = useState(() =>
    (externalSelectedCatalog?.products || []).slice(0, MAX_CATALOG_PRODUCTS)
  );
  const [catalogMenuAnchor, setCatalogMenuAnchor] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [expandedDescriptions, setExpandedDescriptions] = useState(() => new Set());

  // Note: Catalog fetching is handled by the parent component (ChooseActionDrawer)
  // to prevent duplicate API calls. This component only displays catalogs that are
  // already loaded in Redux state.

  // Sync with the value the parent holds (e.g. reopening a saved action). A null value is
  // ignored so a pending, unconfirmed pick isn't reset.
  useEffect(() => {
    if (!externalSelectedCatalog) return;
    setCatalogId(catalogKey(externalSelectedCatalog));
    setSelectedProducts((externalSelectedCatalog.products || []).slice(0, MAX_CATALOG_PRODUCTS));
  }, [externalSelectedCatalog]);

  // Default to the first catalog once the list loads.
  useEffect(() => {
    if (!catalogId && catalogs.length > 0) {
      setCatalogId(catalogKey(catalogs[0]));
    }
  }, [catalogId, catalogs]);

  // The live catalog from Redux (full product list); falls back to the parent's saved value
  // when that catalog isn't loaded, so a saved selection still renders.
  const activeCatalog = useMemo(() => {
    const live = catalogs.find((catalog) => catalogKey(catalog) === catalogId);
    if (live) return live;
    return catalogKey(externalSelectedCatalog) === catalogId ? externalSelectedCatalog : null;
  }, [catalogs, catalogId, externalSelectedCatalog]);

  const products = useMemo(() => activeCatalog?.products || [], [activeCatalog]);
  const visibleProducts = useMemo(() => filterProductsBySearch(products, productSearch), [products, productSearch]);
  // What "Select All" acts on: the products the search currently shows, up to the 30 limit.
  const selectAllTargets = useMemo(() => visibleProducts.slice(0, MAX_CATALOG_PRODUCTS), [visibleProducts]);

  const isPicked = useCallback(
    (product) => selectedProducts.some((p) => productKey(p) === productKey(product)),
    [selectedProducts]
  );
  const allTargetsSelected = selectAllTargets.length > 0 && selectAllTargets.every(isPicked);

  const savedKeys = catalogKey(externalSelectedCatalog) === catalogId
    ? (externalSelectedCatalog?.products || []).map(productKey)
    : [];
  const isConfirmed =
    selectedProducts.length > 0 &&
    selectedProducts.length === savedKeys.length &&
    selectedProducts.every((p) => savedKeys.includes(productKey(p)));

  const handleCatalogChange = (catalog) => {
    setCatalogId(catalogKey(catalog));
    setSelectedProducts([]);
    setProductSearch("");
    setExpandedDescriptions(new Set());
    setCatalogMenuAnchor(null);
  };

  const handleProductToggle = (product) => {
    const key = productKey(product);
    setSelectedProducts((prev) => {
      if (prev.some((p) => productKey(p) === key)) return prev.filter((p) => productKey(p) !== key);
      if (prev.length >= MAX_CATALOG_PRODUCTS) return prev;
      return [...prev, product];
    });
  };

  // Applies to the products currently shown by the search, keeping picks made under another search.
  const handleSelectAll = () => {
    if (allTargetsSelected) {
      const targetKeys = selectAllTargets.map(productKey);
      setSelectedProducts((prev) => prev.filter((p) => !targetKeys.includes(productKey(p))));
      return;
    }
    setSelectedProducts((prev) =>
      [...prev, ...selectAllTargets.filter((product) => !prev.some((p) => productKey(p) === productKey(product)))]
        .slice(0, MAX_CATALOG_PRODUCTS)
    );
  };

  const toggleDescription = (key) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleConfirmSelection = () => {
    if (selectedProducts.length === 0 || !activeCatalog) return;
    const productsToSend = selectedProducts.slice(0, MAX_CATALOG_PRODUCTS);
    onCatalogSelect?.({
      ...activeCatalog,
      sectionTitle: activeCatalog.name ? activeCatalog.name.substring(0, MAX_SECTION_TITLE) : "Products",
      products: productsToSend,
      type: productsToSend.length === 1 ? "single" : "multi",
    });
  };

  // Refresh — use same platform so backend returns catalog for current channel
  const handleRefresh = () => {
    dispatch(fetchCatalogs(platform));
  };

  const errorMessage =
    typeof error === "object" && error?.message
      ? error.message
      : typeof error === "string"
      ? error
      : getErrorMessage(error) || "An error occurred while fetching catalogs";

  const renderProductCard = (product) => {
    const key = productKey(product);
    const isSelected = isPicked(product);
    const atLimit = !isSelected && selectedProducts.length >= MAX_CATALOG_PRODUCTS;
    const isExpanded = expandedDescriptions.has(key);
    return (
      <Box
        key={key}
        onClick={() => !atLimit && handleProductToggle(product)}
        sx={{
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: "10px",
          overflow: "hidden",
          cursor: atLimit ? "not-allowed" : "pointer",
          opacity: atLimit ? 0.5 : 1,
          backgroundColor: "background.paper",
          "&:hover": { borderColor: atLimit ? theme.palette.divider : theme.palette.primary.main },
        }}
      >
        <Box sx={{ position: "relative", width: "100%", aspectRatio: "1 / 1", backgroundColor: "action.hover" }}>
          {hasValidImage(product.imageUrl) ? (
            <Box
              component="img"
              src={product.imageUrl}
              alt={product.name}
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Gallery size={28} color={theme.palette.text.secondary} variant="Linear" />
            </Box>
          )}
          <Checkbox
            checked={isSelected}
            disabled={atLimit}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleProductToggle(product)}
            size="small"
            sx={{
              position: "absolute",
              top: 4,
              left: 4,
              p: 0.375,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              borderRadius: "6px",
              "&:hover": { backgroundColor: theme.palette.background.paper },
            }}
          />
        </Box>

        <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.25, flex: 1 }}>
          <Typography noWrap title={product.name || undefined} sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary" }}>
            {product.name || "Unnamed Product"}
          </Typography>
          {product.description && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                display: "-webkit-box",
                WebkitLineClamp: isExpanded ? "unset" : 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {product.description}
            </Typography>
          )}
          {product.description && product.description.length > 60 && (
            <Typography
              component="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleDescription(key);
              }}
              sx={{
                alignSelf: "flex-start",
                border: "none",
                background: "none",
                p: 0,
                font: "inherit",
                fontSize: "0.75rem",
                color: "primary.main",
                cursor: "pointer",
              }}
            >
              {isExpanded ? "See Less" : "See More"}
            </Typography>
          )}
          {/* Meta's price field already comes back as "amount + currency" (e.g. "500.00 PKR"),
              so no separate currency badge -- same as InboxCatalogSend.jsx. */}
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "text.primary", mt: "auto", pt: 0.5 }}>
            {product.price}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderBody = () => {
    if (status === "loading") {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (status === "failed") {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography sx={{ fontSize: "0.875rem", color: "error.main", fontWeight: 600 }}>
            Failed to load catalogs
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.5 }}>{errorMessage}</Typography>
          {typeof error === "object" && error?.help && (
            <Box sx={{ mt: 1.5, textAlign: "left" }}>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>{error.help.message}</Typography>
              {error.help.steps && (
                <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                  {error.help.steps.map((step, index) => (
                    <Typography key={index} component="li" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      {step}
                    </Typography>
                  ))}
                </Box>
              )}
              {error.help.documentation && (
                <Typography
                  component="a"
                  href={error.help.documentation}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: "0.75rem", color: "primary.main" }}
                >
                  Learn more
                </Typography>
              )}
            </Box>
          )}
          <Button size="small" variant="outlined" onClick={handleRefresh} sx={{ ...buttonSx, mt: 1.5 }}>
            Try Again
          </Button>
        </Box>
      );
    }

    if (catalogs.length === 0 && !activeCatalog) {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <ShoppingCart size={40} color={theme.palette.text.secondary} variant="Linear" style={{ opacity: 0.5 }} />
          <Typography sx={{ mt: 1.5, fontSize: "0.875rem", color: "text.secondary" }}>
            {NO_CATALOGS_HINT[platform] || NO_CATALOGS_HINT.whatsapp}
          </Typography>
          <Button size="small" variant="outlined" onClick={handleRefresh} sx={{ ...buttonSx, mt: 1.5 }}>
            Refresh Catalogs
          </Button>
        </Box>
      );
    }

    return (
      <>
        <TextField
          size="small"
          fullWidth
          placeholder="Search for products..."
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchNormal1 size={16} color={theme.palette.text.secondary} variant="Linear" style={{ marginRight: 6, flexShrink: 0 }} />
            ),
          }}
          sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8125rem" } }}
        />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {selectedProducts.length}/{MAX_CATALOG_PRODUCTS} selected
            {selectedProducts.length === 1 && " · single product message"}
            {selectedProducts.length > 1 && " · multi-product message"}
          </Typography>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={selectAllTargets.length === 0}
            sx={{ ...buttonSx, fontSize: "0.75rem", py: 0, flexShrink: 0 }}
          >
            {allTargetsSelected ? "Deselect All" : "Select All"}
          </Button>
        </Box>

        {visibleProducts.length === 0 ? (
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", fontStyle: "italic", py: 2 }}>
            {products.length === 0 ? "No products in this catalog." : "No products match your search."}
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: columns
                ? `repeat(${columns}, 1fr)`
                : `repeat(auto-fill, minmax(${minProductWidth ?? (compactLayout ? 120 : 140)}px, 1fr))`,
              gap: 1.25,
              maxHeight: 420,
              overflowY: "auto",
              pr: 0.5,
              scrollbarGutter: "stable",
            }}
          >
            {visibleProducts.map(renderProductCard)}
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmSelection}
            disabled={selectedProducts.length === 0 || isConfirmed}
            startIcon={<TickCircle size={16} color="currentColor" variant="Bold" />}
            sx={buttonSx}
          >
            {isConfirmed ? "Selection saved" : `Confirm selection${selectedProducts.length > 0 ? ` (${selectedProducts.length})` : ""}`}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ p: compactLayout ? 0.5 : 1 }}>
      {/* Catalog picker (only when there's a choice) + refresh. owned_product_catalogs lists every
          catalog the Business Manager owns, so the agent can switch if the default isn't the one
          connected to this account. */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
        {catalogs.length > 1 ? (
          <>
            <Button
              size="small"
              onClick={(e) => setCatalogMenuAnchor(e.currentTarget)}
              endIcon={<ArrowDown2 size={14} color="currentColor" variant="Linear" />}
              sx={{
                ...buttonSx,
                color: "text.primary",
                border: `1px solid ${theme.palette.divider}`,
                px: 1.25,
                maxWidth: 260,
              }}
            >
              <Typography component="span" noWrap sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
                {activeCatalog?.name || "Select Catalog"}
              </Typography>
            </Button>
            <Menu anchorEl={catalogMenuAnchor} open={!!catalogMenuAnchor} onClose={() => setCatalogMenuAnchor(null)}>
              {catalogs.map((catalog) => (
                <MenuItem key={catalogKey(catalog)} selected={catalogKey(catalog) === catalogId} onClick={() => handleCatalogChange(catalog)}>
                  {catalog.name}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.secondary", maxWidth: 260 }}>
            {activeCatalog?.name}
          </Typography>
        )}
        <IconButton size="small" aria-label="Refresh catalogs" onClick={handleRefresh} disabled={status === "loading"}>
          <Refresh size={18} color="currentColor" variant="Linear" />
        </IconButton>
      </Box>

      {renderBody()}
    </Box>
  );
};

export default CatalogSend;
